import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import Reveal from '../../components/Reveal/Reveal';
import ArrowIcon from '../../components/icons/ArrowIcon';
import {
  IconWeb, IconEnterprise, IconMobile, IconCloud, IconApi,
  IconTarget, IconShield, IconScale,
} from '../../components/icons/ServiceIcons';
import './Technologies.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const HERO_BG = { '--hero-bg': `url(${PUBLIC_URL}/assets/hero-banner.png)` };
const CTA_BG = { '--cta-bg': `url(${PUBLIC_URL}/assets/cta-banner.png)` };

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const STACK_GROUPS = [
  {
    id: 'backend',
    icon: <IconEnterprise size={28} />,
    title: 'Backend',
    text: 'APIs and business logic built for clarity, security, and long-term maintenance.',
    items: ['C# / .NET 8', 'ASP.NET Core', 'Node.js', 'REST APIs'],
  },
  {
    id: 'frontend',
    icon: <IconWeb size={28} />,
    title: 'Frontend',
    text: 'Responsive web interfaces that stay readable for users and developers.',
    items: ['React', 'TypeScript', 'Modern CSS', 'Component-based UI'],
  },
  {
    id: 'mobile',
    icon: <IconMobile size={28} />,
    title: 'Mobile',
    text: 'Cross-platform apps for customers and field teams, from MVP to store release.',
    items: ['React Native', 'MAUI Blazor', 'Store submission support'],
  },
  {
    id: 'cloud',
    icon: <IconCloud size={28} />,
    title: 'Cloud & DevOps',
    text: 'Practical cloud hosting and delivery pipelines for predictable releases.',
    items: ['Microsoft Azure', 'AWS', 'Docker', 'CI/CD pipelines'],
  },
  {
    id: 'data',
    icon: <IconApi size={28} />,
    title: 'Data & Reporting',
    text: 'Reliable storage and reporting so your product and operations stay informed.',
    items: ['SQL Server', 'PostgreSQL', 'Redis', 'Power BI'],
  },
];

const PRINCIPLES = [
  {
    icon: <IconTarget />,
    title: 'Fit the product, not the trend',
    text: 'We pick tools based on your requirements, team skills, and maintenance needs — not hype.',
  },
  {
    icon: <IconShield />,
    title: 'Secure and maintainable defaults',
    text: 'Access control, sensible architecture, and code you can extend after launch.',
  },
  {
    icon: <IconScale />,
    title: 'Proven over experimental',
    text: 'We prefer mature stacks that are reliable to ship with and hire for later.',
  },
];

const Technologies = () => (
  <div className="technologies-page">
    <SEO
      title="Technologies | .NET, React, Azure & More | CodeInventiv Solutions"
      description="Technologies we use to build and ship software: .NET, React, TypeScript, React Native, Azure, AWS, SQL Server, PostgreSQL, and more. Practical stack choices for growing businesses."
      keywords="software technology stack, .NET development, React development, Azure, AWS, SQL Server, React Native, software company Jaipur"
      ogType="website"
    />

    <section className="page-hero" style={HERO_BG}>
      <div className="container page-hero-inner">
        <Reveal>
          <div className="section-eyebrow on-dark">Technologies</div>
          <h1 className="page-hero-title">
            Technologies we use to<br />
            <em>build and ship</em>
          </h1>
          <p className="page-hero-sub">
            We choose proven tools that are reliable to develop with and easier to maintain over
            time — so your product stays practical to run after launch.
          </p>
          <div className="page-hero-points">
            <span><CheckIcon /> .NET, React, and cloud-first delivery</span>
            <span><CheckIcon /> Stack chosen for your product needs</span>
            <span><CheckIcon /> Source code you can own and extend</span>
          </div>
          <div className="page-hero-actions">
            <Link to="/contact" className="btn btn-gold">
              Discuss Your Project
              <ArrowIcon />
            </Link>
            <Link to="/services" className="btn btn-ghost-light">View Services</Link>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="section section-light" id="stack-groups">
      <div className="container">
        <Reveal className="section-head">
          <div className="section-eyebrow">Our Stack</div>
          <h2 className="section-title">Grouped by how we <em>actually deliver</em></h2>
          <p className="section-sub">
            These are the core technologies we use across custom software, web apps, mobile
            products, and cloud setups.
          </p>
        </Reveal>
        <div className="tech-groups-grid">
          {STACK_GROUPS.map((group) => (
            <Reveal key={group.id} as="article" id={group.id} className="tech-group-card">
              <div className="tech-group-icon">{group.icon}</div>
              <h3>{group.title}</h3>
              <p>{group.text}</p>
              <ul className="tech-group-items">
                {group.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section section-pale" id="how-we-choose">
      <div className="container">
        <Reveal className="section-head">
          <div className="section-eyebrow">How We Choose</div>
          <h2 className="section-title">Practical stack decisions for <em>real products</em></h2>
          <p className="section-sub">
            The right technology depends on scope, timeline, integrations, and who will maintain the
            product. We recommend clearly — and explain why.
          </p>
        </Reveal>
        <div className="tech-principles-grid">
          {PRINCIPLES.map((item) => (
            <Reveal key={item.title} as="article" className="tech-principle-card">
              <div className="tech-principle-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section-dark tech-pill-band">
      <div className="container">
        <Reveal className="section-head">
          <div className="section-eyebrow on-dark">At a Glance</div>
          <h2 className="section-title on-dark">Tools we work with <em>day to day</em></h2>
        </Reveal>
        <Reveal className="tech-pill-grid">
          {[
            'C# / .NET 8', 'ASP.NET Core', 'React', 'TypeScript', 'Node.js',
            'Microsoft Azure', 'AWS', 'SQL Server', 'PostgreSQL', 'Redis',
            'Docker', 'MAUI Blazor', 'React Native', 'REST APIs', 'Power BI',
          ].map((tech) => (
            <span key={tech} className="tech-pill">{tech}</span>
          ))}
        </Reveal>
      </div>
    </section>

    <section className="cta-band" style={CTA_BG}>
      <div className="container cta-inner">
        <Reveal className="cta-copy">
          <h2>Have a preferred stack — or need a <em>recommendation?</em></h2>
          <p>
            Share your product goals and constraints. We will suggest a practical approach and
            next steps — at no cost.
          </p>
        </Reveal>
        <Reveal className="cta-actions">
          <Link to="/contact" className="btn btn-gold">
            Get a Quote
            <ArrowIcon />
          </Link>
          <Link to="/about" className="btn btn-ghost-light">About Us</Link>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Technologies;
